import React from 'react';
import PropTypes from 'prop-types';
import cn from 'utils/cn';

/**
 * PurchaseCompleteBannerLayout component.
 * Render a purchase complete promo banner with image on the left and content on the right.
 * @param {object} props - className, imageSrc, imageAlt, bannerHref, onBannerClick, imageFill, children.
 * @param {string} props.className - Additional CSS classes for the banner container.
 * @param {string} props.imageSrc - Image source URL.
 * @param {string} props.imageAlt - Image alt text.
 * @param {string} props.bannerHref - Link URL for the entire banner.
 * @param {Function} props.onBannerClick - Optional click handler for analytics.
 * @param {boolean} props.imageFill - If true, image covers the left column (object-cover).
 * @param {string} props.imageColumnSize - Image column width: compact (two banners) or expanded (single banner).
 * @param {React.ReactNode} props.children - Text content rendered on the right side.
 * @returns {JSX.Element} The rendered banner layout.
 */
function PurchaseCompleteBannerLayout({
  className,
  imageSrc,
  imageAlt,
  bannerHref,
  onBannerClick,
  imageFill,
  imageColumnSize,
  children,
}) {
  const isExpandedImage = imageColumnSize === 'expanded';

  const containedImage = (
    <img
      src={imageSrc}
      alt={imageAlt}
      className={cn(
        'w-full rounded-lg object-contain object-center',
        isExpandedImage ? 'max-h-[4.5rem]' : 'max-h-12',
      )}
    />
  );

  const filledImage = (
    <img src={imageSrc} alt={imageAlt} className="h-full w-full object-cover object-center" />
  );

  const image = imageFill ? filledImage : containedImage;

  const imageColumnClassName = cn(
    imageFill
      ? 'relative min-h-[5rem] flex-shrink-0 self-stretch overflow-hidden'
      : 'flex flex-shrink-0 items-center justify-center p-2',
    isExpandedImage && 'w-1/2 max-w-[50%]',
    !isExpandedImage && imageFill && 'w-[36%] max-w-[6.5rem]',
    !isExpandedImage && !imageFill && 'w-[30%] max-w-[5.5rem]',
  );

  const bannerClassName = cn(
    'flex h-full min-h-[5.5rem] min-w-0 overflow-hidden rounded-xl border border-gray-300 no-underline text-inherit',
    imageFill ? 'items-stretch' : 'items-center',
    className,
  );

  return (
    <a
      href={bannerHref}
      target="_blank"
      rel="noopener noreferrer"
      onClick={onBannerClick}
      className={bannerClassName}
    >
      <div className={imageColumnClassName}>{image}</div>
      <div
        className={cn(
          'flex min-w-0 flex-1 flex-col justify-center gap-0.5 break-words py-2 pr-4',
          isExpandedImage ? 'pl-3' : 'pl-1',
        )}
      >
        {children}
      </div>
    </a>
  );
}

PurchaseCompleteBannerLayout.propTypes = {
  className: PropTypes.string,
  imageSrc: PropTypes.string.isRequired,
  imageAlt: PropTypes.string.isRequired,
  bannerHref: PropTypes.string.isRequired,
  onBannerClick: PropTypes.func,
  imageFill: PropTypes.bool,
  imageColumnSize: PropTypes.oneOf(['compact', 'expanded']),
  children: PropTypes.node.isRequired,
};

PurchaseCompleteBannerLayout.defaultProps = {
  className: '',
  onBannerClick: null,
  imageFill: false,
  imageColumnSize: 'compact',
};

export default PurchaseCompleteBannerLayout;
